import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Users, Target, BookOpen, CheckCircle2, ArrowRight, CalendarDays } from "lucide-react";

const Stat = ({ label, value, icon: Icon, testid }) => (
  <Card className="p-6 border border-[#E8E9E6] shadow-sm bg-white lift" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="overline">{label}</div>
        <div className="font-heading text-4xl font-medium ink mt-2">{value}</div>
      </div>
      <div className="w-10 h-10 rounded-lg bg-[#EFEAF5] flex items-center justify-center">
        <Icon size={18} className="text-[#6E5AA0]" strokeWidth={1.75} />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline">Welcome back</div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none font-medium ink mt-3">
            Hi, {(user?.name || "Teacher")}.
          </h1>
          <p className="ink-muted mt-3 max-w-xl">
            A calm view of your students, goals, and upcoming meetings — designed to help you focus on what matters most today.
          </p>
        </div>
        <Link to="/students">
          <Button className="bg-[#6E5AA0] hover:bg-[#574785] text-white h-11 px-6" data-testid="dashboard-go-students">
            Go to students <ArrowRight size={16} className="ml-2" />
          </Button>
        </Link>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Stat label="Students" value={loading ? "—" : data?.counts.students ?? 0} icon={Users} testid="stat-students" />
        <Stat label="Active goals" value={loading ? "—" : data?.counts.goals ?? 0} icon={Target} testid="stat-goals" />
        <Stat label="Lessons" value={loading ? "—" : data?.counts.lessons ?? 0} icon={BookOpen} testid="stat-lessons" />
        <Stat label="Lessons completed" value={loading ? "—" : data?.counts.completed_lessons ?? 0} icon={CheckCircle2} testid="stat-completed" />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-3 p-6 sm:p-8 border border-[#E8E9E6] bg-white shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-2xl font-medium ink">Upcoming meetings</h3>
            <Link to="/meetings" className="text-sm text-[#6E5AA0] font-medium hover:underline" data-testid="link-all-meetings">
              View all
            </Link>
          </div>
          <div className="mt-6 space-y-3">
            {loading && <div className="text-sm ink-muted calm-pulse">Loading…</div>}
            {!loading && (data?.upcoming_meetings?.length ?? 0) === 0 && (
              <div className="text-sm ink-muted py-6">No meetings on the horizon. Schedule one from the Meetings tab.</div>
            )}
            {data?.upcoming_meetings?.map((m) => (
              <div key={m.id} className="flex items-start gap-4 p-4 rounded-xl border border-[#EDEDE9] bg-[#FBFBF9]" data-testid={`meeting-row-${m.id}`}>
                <div className="w-10 h-10 rounded-lg bg-white border border-[#E8E9E6] flex items-center justify-center">
                  <CalendarDays size={18} className="text-[#6E5AA0]" />
                </div>
                <div className="flex-1">
                  <div className="font-medium ink">{m.title}</div>
                  <div className="text-xs ink-muted mt-1">
                    {new Date(m.scheduled_at).toLocaleString()} · {m.duration_minutes} min
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-2 p-6 sm:p-8 border border-[#E8E9E6] bg-white shadow-sm">
          <h3 className="font-heading text-2xl font-medium ink">Recent progress</h3>
          <div className="mt-6 space-y-3">
            {loading && <div className="text-sm ink-muted calm-pulse">Loading…</div>}
            {!loading && (data?.recent_progress?.length ?? 0) === 0 && (
              <div className="text-sm ink-muted py-6">No data points yet. Start logging goal progress from a student's Goals tab.</div>
            )}
            {data?.recent_progress?.slice(0, 6).map((p) => (
              <div key={p.id} className="flex items-center justify-between border-b border-[#EDEDE9] pb-3 last:border-0" data-testid={`progress-row-${p.id}`}>
                <div className="text-sm ink truncate pr-4">{p.note || "Data point"}</div>
                <div className="text-sm font-medium text-[#6E5AA0]">{Math.round(p.score)}%</div>
              </div>
            ))}
          </div>
        </Card>
      </section>
    </div>
  );
}
